import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { groupToken } from 'go-modules/models/group-dep/group.factory';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { NgxGroupService, Org } from '@go-modules/services/group/group.service';
import { BehaviorSubject, Observable, finalize, map, tap } from 'rxjs';
import { UseType } from 'go-modules/models/use-type/use-type.interface';
import { SignUpService } from '@go-modules/services/sign-up/sign-up.service';
import { TranslateService } from '@ngx-translate/core';
import { UseTypeCategories } from './use-type-categories';

interface GroupedUseType {
	name: string;
	types: UseType[];
}

@Component({
	selector: 'admin-panel',
	templateUrl: './admin-panel.component.html',
	styleUrls: ['./admin-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AdminPanelComponent implements OnInit, OnDestroy{
	public form = new UntypedFormGroup({
		accountName: new UntypedFormControl('', [Validators.required]),
		useType: new UntypedFormControl(null, [Validators.required])
	});
	public useTypes$: Observable<GroupedUseType[]>;
	public orgs$: Observable<Org[]>;
	public orgsLoading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
	public selectedOrg: Org;

	constructor (
		private groupService: NgxGroupService,
		private ngxGoToastService: NgxGoToastService,
		private signUpService: SignUpService,
		private translate: TranslateService,
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(groupToken) private group,
		private routerService: RouterService
	) {}

	public ngOnInit () {
		this.form.controls.accountName.setValue(this.selectedService.getAccount().name);
		this.useTypes$ = this.signUpService.getUseTypes().pipe(
			map((useTypes) => {
				const selectedUseType = useTypes.find((useType) => {
					return useType.use_type_id === this.selectedService.getAccount().use_type_id;
				});
				this.form.controls.useType.setValue(selectedUseType);

				return Object.values(useTypes.reduce((result, item) => {
					const { category } = item;
					if (!result[category]) {
						result[category] = { name: this.getTranslatedCategory(category), types: [] };
					}
					result[category].types.push(item);
					return result;
				}, {}));
			})
		);
		this.orgs$ = this.groupService.getAllOrgs(false).pipe(
			tap((orgs) => {
				orgs.sort((a, b) => a.name.localeCompare(b.name));
				this.selectedOrg = orgs.find((org) => org.group_id === this.selectedService.getAccount().org_id);
			}),
			finalize(() => {
				this.orgsLoading$.next(false);
			})
		);
	}

	public ngOnDestroy (): void {
		this.orgsLoading$.complete();
	}

	public saveAccountName () {
		const selectedAccount = this.selectedService.getAccount();
		if (!this.form.controls.accountName.valid) {
			return;
		}

		if (this.form.controls.accountName.value === selectedAccount.name) {
			return;
		}

		this.saveAccount({
			...selectedAccount,
			name: this.form.controls.accountName.value
		});
	}

	public updateUseType () {
		const selectedAccount = this.selectedService.getAccount();

		if (this.form.controls.useType.value.use_type_id === selectedAccount.use_type_id) {
			return;
		}

		this.saveAccount({
			...selectedAccount,
			use_type_id: this.form.controls.useType.value.use_type_id
		});
	}


	public moveAccount () {
		this.groupService.moveGroup(this.selectedService.getAccount().group_id, {
			new_parent_id: this.selectedOrg.group_id
		}).subscribe({
			next: (res) => {
				this.selectedService.setAccount(this.group.model(res));
				this.selectedService.setOrg(this.group.model(this.selectedOrg));
				this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_FOLDERS, {id: this.selectedOrg.group_id});
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.SUCCESS,
					message: 'admin-panel_move-success'
				});
			},
			error: () => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.ERROR,
					message: 'common-save-failed'
				});
			}
		});
	}

	public openCreditLedger () {
		const url = `${clientSettings.AdminToolsUrl}/#/global/reports/credit-ledger?beid=${this.selectedService.getAccount().billing_entity_id}`;
		window.open(url, '_blank');
	}

	private getTranslatedCategory (category: string) {
		const translationMap = {
			[UseTypeCategories.OTHER]: 'use-type_other',
			[UseTypeCategories.HEALTHCARE_TRAINING]: 'use-type_healthcare-training',
			[UseTypeCategories.LANGUAGE]: 'use-type_language',
			[UseTypeCategories.COMMUNICATION]: 'use-type_communication',
			[UseTypeCategories.TEACHER_TRAINING]: 'use-type_teacher-training'
		};

		return this.translate.instant(translationMap[category]);
	}

	private saveAccount (account) {
		this.groupService.updateAccount(account.group_id, account).subscribe({
			next: (res) => {
				this.selectedService.setAccount(this.group.model(res));
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.SUCCESS,
					message: 'common-save-success'
				});
			},
			error: () => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.ERROR,
					message: 'common-save-failed'
				});
			}
		});
	}
}
